
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SparklesIcon, UploadIcon, BrushIcon, MenuIcon, XIcon } from '../components/icons';
import logo from '../logo.png';
import { Starfield } from '../components/Starfield';
import { ImageMarquee } from '../components/ImageMarquee';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { APP_VERSION } from '../config/version';

const LandingPage = () => {
    const navigate = useNavigate();
    const { session } = useAuth();
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

    return (
        <div className="min-h-screen text-white selection:bg-indigo-500/30 relative">
            <Starfield />
            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-black/50 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={logo} alt="Dotting Logo" className="w-8 h-8 object-contain" />
                        <span className="text-xl font-bold tracking-tight">DOTTING</span>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-400">
                        <a href="#features" className="hover:text-white transition-colors">{t('nav.features')}</a>
                        <a href="#how-it-works" className="hover:text-white transition-colors">{t('nav.how_it_works')}</a>
                    </div>
                    <div className="hidden md:flex items-center gap-4">
                        <LanguageSwitcher />
                        {session ? (
                            <Link to="/app" className="text-sm font-bold bg-white text-black px-5 py-2.5 rounded-full hover:bg-gray-100 transition-transform hover:scale-105">
                                {t('nav.go_to_app')}
                            </Link>
                        ) : (
                            <>
                                <Link to="/login" className="text-sm font-medium text-gray-400 hover:text-white transition-colors">{t('nav.login')}</Link>
                                <Link to="/register" className="text-sm font-bold bg-white text-black px-5 py-2.5 rounded-full hover:bg-gray-100 transition-transform hover:scale-105">
                                    {t('nav.get_started')}
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="md:hidden text-white p-2"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                        {isMobileMenuOpen ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Menu Dropdown */}
                {isMobileMenuOpen && (
                    <div className="md:hidden absolute top-20 left-0 w-full bg-black/95 border-b border-white/10 backdrop-blur-xl p-6 flex flex-col gap-6 animate-fade-in-down">
                        <div className="flex flex-col gap-4 text-base font-medium text-gray-400">
                            <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-white transition-colors">{t('nav.features')}</a>
                            <a href="#how-it-works" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-white transition-colors">{t('nav.how_it_works')}</a>
                        </div>
                        <div className="h-px bg-white/10" />
                        <div className="flex flex-col gap-4">
                            {session ? (
                                <Link to="/app" className="text-center py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-100 transition-colors">
                                    {t('nav.go_to_app')}
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login" className="text-center py-3 text-gray-300 hover:text-white font-medium border border-white/10 rounded-xl hover:bg-white/5 transition-colors">
                                        {t('nav.login')}
                                    </Link>
                                    <Link to="/register" className="text-center py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-100 transition-colors">
                                        {t('nav.get_started')}
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
                {/* Subtle top glare, mostly transparent to show stars */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/20 to-transparent" />

                <div className="relative max-w-7xl mx-auto px-6 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-indigo-300 mb-8 animate-fade-in-up">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                        </span>
                        v{APP_VERSION} Now Live
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 text-white max-w-5xl mx-auto leading-[1.1] animate-fade-in-up">
                        {t('landing.title')} <br className="hidden md:block" />
                        <span className="text-indigo-300">{t('landing.title_sub')}</span>
                    </h1>

                    <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed">
                        {t('landing.description')}
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/register" className="w-full sm:w-auto px-8 py-4 bg-white text-black text-lg font-bold rounded-full hover:bg-gray-100 transition-all hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)]">
                            {t('landing.start_free')}
                        </Link>
                        <a href="#features" className="w-full sm:w-auto px-8 py-4 bg-white/5 border border-white/10 text-white text-lg font-bold rounded-full hover:bg-white/10 transition-all backdrop-blur-sm">
                            {t('landing.watch_demo')}
                        </a>
                    </div>
                </div>

                {/* Dynamic Image Showcase */}
                <div className="relative mt-20 mb-20">
                    <div className="absolute inset-0 bg-indigo-500/5 blur-[100px] -z-10" />
                    <ImageMarquee />
                </div>
            </section>

            {/* Features Grid - Fully transparent background to show stars */}
            <section id="features" className="py-32 border-t border-white/5 bg-transparent">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-24">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6">{t('landing.features_title')}</h2>
                        <p className="text-gray-400 max-w-2xl mx-auto">{t('landing.features_desc')}</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <UploadIcon className="w-6 h-6 text-indigo-400" />,
                                title: t('landing.feature1_title'),
                                desc: t('landing.feature1_desc')
                            },
                            {
                                icon: <BrushIcon className="w-6 h-6 text-purple-400" />,
                                title: t('landing.feature2_title'),
                                desc: t('landing.feature2_desc')
                            },
                            {
                                icon: <SparklesIcon className="w-6 h-6 text-pink-400" />,
                                title: t('landing.feature3_title'),
                                desc: t('landing.feature3_desc')
                            }
                        ].map((feature, i) => (
                            <div key={i} className="p-8 rounded-3xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors group">
                                <div className="w-12 h-12 rounded-2xl bg-black border border-white/10 flex items-center justify-center mb-6 group-hover:border-indigo-500/30 transition-colors">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
                                <p className="text-gray-400 leading-relaxed">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/5 py-12 bg-black/80 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex flex-col md:flex-row items-center gap-2 text-gray-500 text-sm">
                        <p>{t('landing.footer_rights')}</p>
                        <span className="hidden md:inline">•</span>
                        <p>{t('landing.footer_location')}</p>
                    </div>
                    <div className="flex gap-6 text-sm text-gray-500">
                        <span className="hover:text-white transition-colors cursor-default">Privacy</span>
                        <span className="hover:text-white transition-colors cursor-default">Terms</span>
                        <span className="hover:text-white transition-colors cursor-default">Twitter</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
